import { Button, Loading, Table } from '@octano/global-ui';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col, Row } from 'reactstrap';

import DisplayError from '../../../../../components/info/DisplayError';
import { useStepState } from '../../../../../components/step/useStepState';
import { SectionTitle } from '../../../../../components/text';
import TableEmptyContent from '../../../../../components/text/TableEmptyContent';
import { TranslationsKeys } from '../../../../../locales/translations';
import SectionTeachersModal, {
  SectionTeachersModalMethods,
} from '../../../Sections/parts/SectionTeachersModal';
import { updateAcademicOfferCurrentStep } from '../../api';
import { useAcademicOfferWizard } from '../../context/AcademicOfferWizardContext';
import {
  NonStructuredAcademicOfferStatus,
  NonStructuredAcademicOfferStepEnum,
} from '../../types';
import {
  CoursesWithSectionsFormDataLoaderProvider,
  useCoursesSectionsLoader,
} from './Loaders/CoursesSectionsLoader';
import { useCoursesColumns } from './hooks/useCoursesColumns';
import ModalAssignSection, {
  ModalAssignSectionMethods,
} from './parts/ModalAssignSection';
import { CourseWithSection } from './types';

export function AcademicOfferSections() {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const { nextStep, prevStep } = useStepState();
  const { ceAcademicOfferId, offerStatus } = useAcademicOfferWizard();
  const isClosed = offerStatus === NonStructuredAcademicOfferStatus.Cerrada;

  const { loading, result } = useCoursesSectionsLoader();
  const { refresh } = useCoursesSectionsLoader();

  const modalAssignSectionRef = useRef<ModalAssignSectionMethods>(null);

  const handleAssignSection = (course: CourseWithSection) => {
    modalAssignSectionRef?.current?.open(course);
  };

  const handleSectionAssigned = () => {
    refresh();
  };

  const sectionTeachersModalRef = useRef<SectionTeachersModalMethods>(null);

  const handleOpenTeachersModal = (name: string, teachers: string[]) => {
    sectionTeachersModalRef.current?.open(name, teachers);
  };

  const columns = useCoursesColumns({
    onAssignSection: handleAssignSection,
    onOpenTeachersModal: handleOpenTeachersModal,
    isReadOnly: isClosed,
  });

  const handleNext = async () => {
    if (!ceAcademicOfferId) {
      return;
    }

    if (isClosed) {
      nextStep();
      return;
    }

    await updateAcademicOfferCurrentStep(
      ceAcademicOfferId,
      NonStructuredAcademicOfferStepEnum.Schedule,
    );
    nextStep();
  };

  const handleBack = async () => {
    if (!ceAcademicOfferId) {
      return;
    }

    if (isClosed) {
      prevStep();
      return;
    }

    await updateAcademicOfferCurrentStep(
      ceAcademicOfferId,
      NonStructuredAcademicOfferStepEnum.Offer,
    );
    prevStep();
  };

  if (!result?.success && loading) {
    return (
      <Card>
        <Loading textBody={''} />
      </Card>
    );
  }

  if (!result?.success) {
    return (
      <DisplayError
        insideCard
        title={t(`sections.queryError.title`)}
        textBody={t(`sections.queryError.description`)}
      />
    );
  }

  // Verificar si todas las asignaturas tienen sección asignada
  const allCoursesHaveSection =
    result.data.data.length > 0 &&
    result.data.data.every((c: any) => c?.assignedSection);

  return (
    <>
      <SectionTitle text={t('sections.title')} />
      <p className="fs-16">{t('sections.description')}</p>
      <div className="mb-4">
        <Table
          columns={columns}
          data={result?.data?.data ?? []}
          striped
          noResultsText={
            <TableEmptyContent title={t('sections.noCourses')} subtitle="" />
          }
        />
      </div>

      <Row>
        <Col xs={12} md={6} lg={4} className="offset-md-6 offset-lg-8">
          <Row>
            <Col>
              <Button
                text={t('wizard.back')}
                outlined
                fullwidth
                onClick={handleBack}
              />
            </Col>
            <Col>
              <Button
                text={t('wizard.next')}
                fullwidth
                onClick={handleNext}
                disabled={!isClosed && !allCoursesHaveSection}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      {ceAcademicOfferId && (
        <CoursesWithSectionsFormDataLoaderProvider
          defaultQuery={ceAcademicOfferId}
          fetchImmediately
        >
          <ModalAssignSection
            ref={modalAssignSectionRef}
            ceAcademicOfferId={ceAcademicOfferId}
            onSuccess={handleSectionAssigned}
          />
        </CoursesWithSectionsFormDataLoaderProvider>
      )}

      <SectionTeachersModal ref={sectionTeachersModalRef} />
    </>
  );
}
